<template>
  <v-dialog :value="value" max-width="560" @input="$emit('input', $event)" @keydown.esc="close">
    <v-card>
      <v-card-title class="subtitle-1 py-2">{{ $t('companyApproval.palette_title') }}</v-card-title>
      <v-divider/>
      <div class="pa-3">
        <v-text-field ref="search" v-model="query"
                      :placeholder="$t('companyApproval.palette_search')"
                      autofocus dense hide-details prepend-inner-icon="mdi-magnify"
                      @keydown.down.prevent="move(1)"
                      @keydown.up.prevent="move(-1)"
                      @keydown.enter.prevent="choose()"/>
      </div>
      <v-divider/>
      <v-list class="py-0" dense max-height="340" style="overflow-y:auto">
        <v-list-item v-for="(t, i) in results" :key="t.value"
                     :input-value="i === index"
                     @click="choose(t)">
          <v-list-item-title>{{ t.text }}</v-list-item-title>
          <!-- The value this field holds on this claim, where there is one:
               "اسم المطالب" says less than the name it will print. Fields with
               nothing behind them (a template, say) show the {token}. -->
          <v-list-item-subtitle :class="hasValue(t) ? 'ms-3' : 'ltr-text ms-3 grey--text'">
            {{ hasValue(t) ? t.resolved : t.token }}
          </v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="!results.length">
          <v-list-item-title class="grey--text">{{ $t('companyApproval.palette_empty') }}</v-list-item-title>
        </v-list-item>
      </v-list>
      <v-divider/>
      <div class="caption grey--text pa-2 text-center">{{ $t('companyApproval.palette_hint') }}</div>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * A searchable list of the {fields} a text can carry.
 *
 * Shared by the letter templates and the message to the company so both offer
 * the same thing: 25 fields are too many to scan down a menu, and the one you
 * want is easier to type than to find.
 *
 * It only picks - the parent decides where the token goes, since only the
 * parent knows which editor the caret was in.
 */
export default {
  name: 'FieldPalette',
  props: {
    value: {type: Boolean, default: false},
    tokens: {type: Array, default: () => []}
  },
  data() {
    return {query: '', index: 0, picked: false};
  },
  computed: {
    results() {
      const q = String(this.query || '').trim().toLowerCase();
      if (!q) return this.tokens;
      // Matches the Arabic label as well as the {token}, so either way of
      // thinking about a field finds it.
      return this.tokens.filter(t =>
          String(t.text || '').toLowerCase().indexOf(q) !== -1
          || String(t.token || '').toLowerCase().indexOf(q) !== -1);
    }
  },
  watch: {
    value(open) {
      if (!open) {
        // Closed without choosing anything - Esc, the backdrop, a change of
        // mind. The parent has to put the caret back where it took it from.
        if (!this.picked) this.$emit('cancel');
        return;
      }
      this.query = '';
      this.index = 0;
      this.picked = false;
      // autofocus alone loses the race with the dialog's own focus trap.
      this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
    },
    /** A narrowed list must not keep a highlight that is now out of range. */
    query() {
      this.index = 0;
    }
  },
  methods: {
    /** @returns {boolean} whether this field resolves to something on this claim */
    hasValue(token) {
      return String(token.resolved == null ? '' : token.resolved).trim() !== '';
    },
    close() {
      this.$emit('input', false);
    },
    move(step) {
      const n = this.results.length;
      if (!n) return;
      this.index = (this.index + step + n) % n;
    },
    /** @param {object} [token] the row clicked; otherwise the highlighted one */
    choose(token) {
      const picked = token || this.results[this.index];
      if (!picked) return;
      this.picked = true;
      this.close();
      // After the dialog closes, so focus is back in the editor before the
      // parent restores the caret and writes the token.
      this.$nextTick(() => this.$emit('select', picked));
    }
  }
};
</script>
